/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IEveCatalogCheckerData } from './eve-catalog-checker.data';
import { IEveCatalogCheckerProps } from './eve-catalog-checker.props.autogenerated';
import { computed } from 'mobx';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import Msdyn365, { getCatalogId } from '@msdyn365-commerce/core';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { GetCatalogIdFromContext } from '../../utilities/get-catalog-id-from-context';

export interface IEveCatalogCheckerViewProps extends IEveCatalogCheckerProps<IEveCatalogCheckerData> {
    modal: React.ReactNode;
}

interface IEveCatalogCheckerState {
    displayModal: boolean;
    isInitialized: boolean;
}

/**
 *
 * EveCatalogChecker component
 * @extends {React.PureComponent<IEveCatalogCheckerProps<IEveCatalogCheckerData>>}
 */
class EveCatalogChecker extends React.PureComponent<IEveCatalogCheckerProps<IEveCatalogCheckerData>, IEveCatalogCheckerState> {
    private static readonly _catalogIdQueryParamKey: string = 'catalogid';
    constructor(props: IEveCatalogCheckerProps<IEveCatalogCheckerData>) {
        super(props);
        this.state = {
            displayModal: false,
            isInitialized: false
        };

        // Set catalog ID in URL before DOM is painted
        if (Msdyn365.isBrowser) {
            const currentCatalogId = getCatalogId(this.props.context.request);
            if (currentCatalogId > 0) {
                const url = new URL(window.location.href);
                const urlCatalogId = parseInt(url.searchParams.get(EveCatalogChecker._catalogIdQueryParamKey) || '0', 10);
                if (urlCatalogId !== currentCatalogId) {
                    url.searchParams.set(EveCatalogChecker._catalogIdQueryParamKey, currentCatalogId.toString());
                    window.location.replace(`${url.pathname}${url.search}`);
                }
            }
        }
    }

    @computed private get catalogId() {
        return GetCatalogIdFromContext(this.props.context.actionContext);
    }

    @computed private get cartCatalogId(): number {
        const {
            data: {
                cart: { result: cart }
            }
        } = this.props;

        if (cart?.cart && ArrayExtensions.hasElements(cart.cart.CartLines)) {
            return cart.cart.CartLines[0].CatalogId || 0;
        }
        return 0;
    }

    public componentDidMount() {
        if (this.catalogId > 0 && this.cartCatalogId !== 0 && this.cartCatalogId !== this.catalogId) {
            // We have to prompt user to change catalogs
            this.setState({ displayModal: true });
        } else if (this.catalogId === 0 && !this.catalogId) {
            // If no catalog Id, we have to set the catalog to the first catalog in the list or the cart catalog Id.
            const {
                data: { catalogs }
            } = this.props;
            const catalogToSwapTo = this.cartCatalogId || catalogs.result?.[0]?.RecordId || 0;
            if (catalogToSwapTo > 0) {
                this._setCatalogId(catalogToSwapTo);
            }
        }
    }

    private _setCatalogId(catalogId: number) {
        //Check we're in a browser
        if (!Msdyn365.isBrowser) {
            return;
        }
        // Initialize Should reload to false.
        let shouldReload = false;
        // Get current url
        const url = new URL(window.location.href);
        // Check if the current page is the search page, or it has a product or category.
        const {
            context: {
                app: {
                    routes: {
                        search: { destinationUrl }
                    }
                },
                request: { urlTokens }
            }
        } = this.props;

        // If current page is the search page, or it has a product or category, we need to reload the page.
        if (url.pathname === destinationUrl) {
            shouldReload = true;
        } else {
            const pageType = urlTokens?.pageType?.toLowerCase();
            if (pageType === 'product' || pageType === 'category') {
                shouldReload = true;
            }
        }
        // Add the catalog Id to the url, or reload the page with the catalog ID
        url.searchParams.set(EveCatalogChecker._catalogIdQueryParamKey, catalogId.toString());
        if (shouldReload) {
            window.location.href = `${url.pathname}${url.search}`;
        } else {
            window.history.pushState({}, '', `${url.pathname}${url.search}`);
        }
    }

    private _doesLinkHaveCatalogIdQueryParam(link: string): boolean {
        return link.toLowerCase().indexOf(EveCatalogChecker._catalogIdQueryParamKey) > -1;
    }

    private _rebuildLinkWithCatalogIdQueryParam(link: string): string {
        let concatSymbol: '&' | '?' = '?';

        if (link.indexOf(concatSymbol) > -1) {
            concatSymbol = '&';
        }

        return `${link}${concatSymbol}${EveCatalogChecker._catalogIdQueryParamKey}=${this.catalogId}`;
    }

    private _addAnchorToTagChildren(element: Element) {
        if (element.nextElementSibling) {
            this._addAnchorToTagChildren(element.nextElementSibling);
        }
        for (let i = 0; i < element.children.length; i++) {
            // eslint-disable-next-line security/detect-object-injection
            const tag = element.children[i];
            if (tag.children.length) {
                this._addAnchorToTagChildren(tag);
            }
            if (tag.tagName.toLowerCase() === 'a') {
                const anchorTag = tag as HTMLAnchorElement;
                if (!this._doesLinkHaveCatalogIdQueryParam(anchorTag.href)) {
                    anchorTag.href = this._rebuildLinkWithCatalogIdQueryParam(anchorTag.href);
                }
            }
        }
    }

    private _isExternalUrl(url: any) {
        return new URL(url).origin !== location.origin;
    }

    private _initialize() {
        if (this.catalogId > 0) {
            // Add catalog ID to all links on the page
            const anchorTags = document.querySelectorAll<HTMLAnchorElement>('a');
            anchorTags.forEach(tag => {
                if (tag.href) {
                    if (!this._isExternalUrl(tag) && !this._doesLinkHaveCatalogIdQueryParam(tag.href)) {
                        tag.href = this._rebuildLinkWithCatalogIdQueryParam(tag.href);
                    }
                }
            });

            // Add event listener to all buttons that render a dropdown menu on click
            const buttons = document.querySelectorAll<HTMLButtonElement>('button[aria-expanded="false"]');
            buttons.forEach(btn => {
                btn.addEventListener('click', () => {
                    setTimeout(() => {
                        this._addAnchorToTagChildren(btn.parentElement!);
                    }, 50);
                });
            });
        }
    }

    private _renderCatalogPromptModal() {
        const {
            resources: { catalogPromptBodyText, catalogPromptHeading, changeCatalogButtonText },
            config: { forceSingleCatalog }
        } = this.props;
        return (
            <Modal isOpen={this.state.displayModal && forceSingleCatalog}>
                <ModalHeader>
                    <span>{catalogPromptHeading}</span>
                </ModalHeader>
                <ModalBody>{catalogPromptBodyText}</ModalBody>
                <ModalFooter>
                    <Button className='change-catalog-button'>
                        <a href={this._buildUrlToNewCatalog(this.cartCatalogId)}>{changeCatalogButtonText}</a>
                    </Button>

                    {/*{this._renderSaveToOrderTemplateButton()}*/}
                </ModalFooter>
            </Modal>
        );
    }

    private _buildUrlToNewCatalog(newCatalogId: number): string {
        if (!Msdyn365.isBrowser) {
            return '';
        }
        const url = new URL(window.location.href);
        url.searchParams.set(EveCatalogChecker._catalogIdQueryParamKey, newCatalogId.toString());
        return `${url.pathname}${url.search}`;
    }

    public render(): JSX.Element | null {
        if (!this.state.isInitialized && Msdyn365.isBrowser) {
            this.setState({ isInitialized: true });
            this._initialize();
        }
        return <>{this._renderCatalogPromptModal()}</>;
    }
}

export default EveCatalogChecker;
